import {useMemo, useState} from 'react';
import {view} from '@sail/ui';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useSelector, setValue} from 'src/v2/state/store';
import {Text} from 'src/v2/components/common/Text';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {SelectionCard} from 'src/v2/components/common/SelectionCard';
import {
  USDC_NETWORK_INFO_MAP,
  type UsdcNetwork,
} from 'src/v2/components/networkSelect/networkSelectConstants';
import {getNetworkIcon, getNetworkName} from 'src/components/Wallet/constants';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';

const messages = defineMessages({
  selectNetwork: {
    id: 'crypto.payins.v2.network_select.select_network',
    description: 'Label for network selection list',
    defaultMessage: 'Select network for {token}',
  },
  continueButton: {
    id: 'crypto.payins.v2.network_select.continue',
    description: 'Continue button on network selection screen',
    defaultMessage: 'Continue',
  },
});

export const NetworkSelectScreen = () => {
  const intl = useIntl();
  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken!,
  );
  const supportedCurrencies = useSelector((state) => state.supportedCurrencies);

  const networkList = useMemo(() => {
    const networks = [
      ...new Set(
        supportedCurrencies
          .filter((c) => c.assetCode === selectedToken)
          .map((c) => c.currencyNetwork),
      ),
    ];
    return networks.filter((n): n is UsdcNetwork => n in USDC_NETWORK_INFO_MAP);
  }, [supportedCurrencies, selectedToken]);

  const [selectedNetwork, setSelectedNetwork] = useState<UsdcNetwork | null>(
    null,
  );

  const handleContinue = () => {
    if (!selectedNetwork) {
      return;
    }

    setValue((state) => {
      state.anyCryptoDeposit.selectedNetwork = selectedNetwork;
      state.anyCryptoDeposit.quote = {
        depositAddress: null,
        inputAmount: null,
        inputChain: null,
        inputCurrency: null,
      };
      state.screen = 'REFUND';
    });
  };

  return (
    <view.div css={{stack: 'y', gap: 'large'}}>
      <view.div css={{stack: 'y', gap: 'small'}}>
        <Text type="bodyEmphasized">
          <FormattedMessage
            {...messages.selectNetwork}
            values={{token: formatAssetCode(selectedToken)}}
          />
        </Text>
        <view.div css={{stack: 'y', gap: 'small'}} role="listbox">
          {networkList.map((network) => {
            const info = USDC_NETWORK_INFO_MAP[network];
            if (!info) return null;
            return (
              <SelectionCard
                key={network}
                icon={getNetworkIcon(network)}
                label={getNetworkName(network)}
                detail={intl.formatMessage(info.processingTimeMessage)}
                selected={selectedNetwork === network}
                onSelect={() => setSelectedNetwork(network)}
              />
            );
          })}
        </view.div>
      </view.div>
      <BrandedButton onClick={handleContinue} disabled={!selectedNetwork}>
        <FormattedMessage {...messages.continueButton} />
      </BrandedButton>
    </view.div>
  );
};
